import React, { useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Bold, Italic, Heading2, List, ListOrdered, Link2, Quote } from 'lucide-react';

const ToolbarButton = ({ onClick, title, testId, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testId}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    className="w-9 h-9 flex items-center justify-center rounded-md text-[#004D51] hover:bg-[#F3CEB2]/50 transition-colors"
  >
    {children}
  </button>
);

export const RichTextEditor = ({ value, onChange }) => {
  const ref = useRef(null);
  const internalUpdate = useRef(false);

  useEffect(() => {
    if (ref.current && !internalUpdate.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = DOMPurify.sanitize(value || '');
    }
    internalUpdate.current = false;
  }, [value]);

  const handleInput = () => {
    internalUpdate.current = true;
    onChange(ref.current.innerHTML);
  };

  const exec = (command, arg = null) => {
    document.execCommand(command, false, arg);
    ref.current.focus();
    handleInput();
  };

  const addLink = () => {
    const url = window.prompt('Enter the link URL:');
    if (url) exec('createLink', url);
  };

  return (
    <div className="border-2 border-[#004D51] rounded-lg overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 border-b-2 border-[#004D51]/20 bg-[#F3CEB2]/20 px-2 py-1">
        <ToolbarButton onClick={() => exec('bold')} title="Bold" testId="rte-bold"><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('italic')} title="Italic" testId="rte-italic"><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('formatBlock', 'h2')} title="Heading" testId="rte-h2"><Heading2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('formatBlock', 'blockquote')} title="Quote" testId="rte-quote"><Quote className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('insertUnorderedList')} title="Bullet list" testId="rte-ul"><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('insertOrderedList')} title="Numbered list" testId="rte-ol"><ListOrdered className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={addLink} title="Insert link" testId="rte-link"><Link2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('formatBlock', 'p')} title="Normal text" testId="rte-p"><span className="text-xs font-bold">P</span></ToolbarButton>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-testid="rte-content"
        className="prose-vesta min-h-[260px] max-h-[460px] overflow-y-auto px-4 py-3 text-[#2b2b2b] focus:outline-none"
      />
    </div>
  );
};

export default RichTextEditor;
